import React from "react";
import { t } from "ttag";

import { IProduct, InternalProduct } from "../../../models/catalogue";
import { isoProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import { IModularConfiguratorOptionSet } from "../models";
import { setSelectedRootProduct } from "../reducers";
import { rootProductSelector } from "../selectors";
import { getDefaultRootProduct } from "../utils";
import { RootProductCompareDrawer } from "./RootProductCompareDrawer";
import { RootProductCompareModal } from "./RootProductCompareModal";

import styles from "./RadioRootProductSelector.module.scss";

function getRootProductTitle(
    rootProduct: InternalProduct,
    isPlpPanelStyle: boolean | undefined,
): string {
    if (isPlpPanelStyle) {
        return rootProduct.subtitle || rootProduct.title;
    } else {
        if (rootProduct.attributes.model_title?.value) {
            return rootProduct.attributes.model_title.value.toString();
        }
        return rootProduct.title;
    }
}

function showCompareLink(
    isPlpPanelStyle: boolean | undefined,
    optionSet: IModularConfiguratorOptionSet,
): boolean {
    if (
        !isPlpPanelStyle ||
        !optionSet.root_product_compare_modal ||
        optionSet.root_product_compare_modal.products.length === 0
    ) {
        return false;
    }

    return true;
}

export const RadioRootProductSelector = (props: {
    optionSet: IModularConfiguratorOptionSet;
    isPlpPanelStyle?: boolean;
}): React.ReactNode => {
    const [isPanelOpen, setIsPanelOpen] = React.useState(false);

    const dispatch = useAppDispatch();
    const configuratorState =
        useAppSelector((rootState) => rootState.configurator) || defaults;
    const rootProducts = configuratorState.entities.rootProducts;
    const selectedRootProduct = rootProductSelector(configuratorState);

    const onOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsPanelOpen(true);
    };

    const onCloseButtonClick = (
        removeStaticFromBody: () => void,
        removePanelStateToBody: () => void,
        showFinanceBlock: () => void,
    ) => {
        setIsPanelOpen(false);
        // Execute cleanup callbacks after state update
        removeStaticFromBody();
        removePanelStateToBody();
        showFinanceBlock();
    };

    const onSelectRootProduct = (product: IProduct) => {
        dispatch(setSelectedRootProduct(product));
    };

    if (rootProducts.length <= 1) {
        return null;
    }

    const selectedProductID = selectedRootProduct?.id
        ? selectedRootProduct.id
        : getDefaultRootProduct(rootProducts).id;

    return (
        <>
            <div className={`${styles.root} configurator__option-container`}>
                <fieldset>
                    <legend>
                        <h6>{props.optionSet.root_product_selector.label}</h6>
                    </legend>
                    {showCompareLink(
                        props.isPlpPanelStyle,
                        props.optionSet,
                    ) && (
                        <>
                            <button className={styles.openBtn} onClick={onOpen}>
                                {t`Compare`}
                            </button>
                        </>
                    )}
                    {!props.isPlpPanelStyle && (
                        <RootProductCompareModal optionSet={props.optionSet} />
                    )}
                    {rootProducts.map((rootProduct) => (
                        <label
                            key={`${rootProduct.id}`}
                            aria-selected={rootProduct.id === selectedProductID}
                        >
                            {/* TODO(i18n): Add aria-label with translated text for screen readers describing the product selection: aria-label={t`Select ${getRootProductTitle(rootProduct, props.isPlpPanelStyle)}`} */}
                            <input
                                type="radio"
                                name="root-product"
                                value={isoProductID.unwrap(rootProduct.id)}
                                className={`ada-screenreader-only`}
                                checked={rootProduct.id === selectedProductID}
                                onChange={(e) => {
                                    if (!e.currentTarget.checked) {
                                        return;
                                    }
                                    onSelectRootProduct(rootProduct);
                                }}
                            />
                            {rootProduct.attributes
                                .configurator_root_product_selector_icon
                                ?.value && (
                                // TODO(i18n): Add translated alt text for product icon: alt={t`${getRootProductTitle(rootProduct, props.isPlpPanelStyle)} icon`}
                                <img
                                    src={`${rootProduct.attributes.configurator_root_product_selector_icon.value}`}
                                    alt=""
                                />
                            )}
                            <span>
                                {getRootProductTitle(
                                    rootProduct,
                                    props.isPlpPanelStyle,
                                )}
                                {rootProduct.attributes.model_subtitle && (
                                    <span className={styles.subTitle}>
                                        {rootProduct.attributes.model_subtitle.value?.toString()}
                                    </span>
                                )}
                            </span>
                        </label>
                    ))}
                </fieldset>
            </div>
            {/* TODO(i18n): panelClass value "model" may need translation if it's user-facing. Verify if this is a CSS class (OK) or displayed text (needs translation) */}
            {isPanelOpen && (
                <RootProductCompareDrawer
                    prefixNameCapped={
                        props.optionSet.root_product_selector.label
                    }
                    panelClass={"model"}
                    optionSet={props.optionSet}
                    isPanelOpen={isPanelOpen}
                    onCloseButtonClick={onCloseButtonClick}
                />
            )}
        </>
    );
};
